import { modelOptions, post, prop } from "@typegoose/typegoose";
import { DataTypeSpecifier } from "../../HistoricalData.js";
import { validate, getZodSchemaFromClass, DataTypeSpecifierString } from "../../zod-decorator/index.js";
import { z } from "zod";
import { type DtoValidatorTypes } from "../validatableClass.js";
import type {
    RecentSensorDataPointFieldsWithDefaults,
    RecentSensorDataPointSchemaTypes,
} from "../types/recentSensorDataPoint.d.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";

const SCHEMA_KEY = "RecentSensorDataPoint" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<RecentSensorDataPoint>>(...addSchemaKeyHook(SCHEMA_KEY))
export class RecentSensorDataPoint<SensorDataType extends DataTypeSpecifier = DataTypeSpecifier> extends SchemaBase<
    RecentSensorDataPoint<SensorDataType>,
    RecentSensorDataPointFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.coerce.date() })
    @prop({ type: () => Date, required: true })
    public date!: Date;

    @validate({ zodValidator: DataTypeSpecifierString })
    @prop({ type: () => String, required: true })
    public dataType!: SensorDataType;

    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public value!: number;

    static getters = {
        // Example getter: returns the value property of the instance.
        getValue: (self: RecentSensorDataPoint) => self.value,
    } as const satisfies Record<string, (self: RecentSensorDataPoint) => any>;

    static utilities = {
        // need a utility function on at least 2 schemas for the typescript to work. Can delete this later
        getAbsoluteTimeDelta: (dataPoint1: RecentSensorDataPoint, dataPoint2: RecentSensorDataPoint) =>
            Math.abs(dataPoint1.date.valueOf() - dataPoint2.date.valueOf()),
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<RecentSensorDataPointSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<
            RecentSensorDataPoint<SensorDataType>,
            RecentSensorDataPointFieldsWithDefaults
        >,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

RecentSensorDataPoint.dtoValidators = getZodSchemaFromClass(
    RecentSensorDataPoint,
) as unknown as DtoValidatorTypes<RecentSensorDataPointSchemaTypes>;
